import React, { useEffect, useRef, useState } from "react";
import AppFooter from "../components/common/footer/AppFooter";

const LAST_UPDATED = "October 20, 2025";

const SECTIONS = [
  { id: "overview", label: "1. Overview" },
  { id: "terms", label: "2. Terms of Service" },
  { id: "privacy", label: "3. Privacy Policy (GDPR)" },
  { id: "contact", label: "4. Contact & Governing Law" },
];

export default function TermsPrivacy() {
  const paneRef = useRef(null);
  const [active, setActive] = useState(SECTIONS[0].id);

  useEffect(() => {
    const pane = paneRef.current;
    if (!pane) return;

    const headings = Array.from(
      pane.querySelectorAll("section[id]")
    );

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the one closest to the top & intersecting
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      {
        root: pane,
        threshold: [0.25, 0.5, 0.75],
      }
    );

    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, []);

  // Smooth scroll for TOC clicks inside the pane
  const onTocClick = (e, id) => {
    e.preventDefault();
    const el = paneRef.current?.querySelector(`#${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <main className="legal-shell">
        <style>{STYLES}</style>

        <header className="legal-header">
          <h1>Baknay Platform — Terms of Service & Privacy Policy</h1>
          <p className="muted">Last updated: {LAST_UPDATED}</p>
        </header>

        <div className="legal-grid">
          {/* TOC */}
          <nav className="legal-toc">
            <div className="card">
              <div className="card-header">On this page</div>
              <ol className="toc-list">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      onClick={(e) => onTocClick(e, s.id)}
                      className={active === s.id ? "active" : ""}
                      aria-current={active === s.id ? "true" : "false"}
                    >
                      {s.label}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </nav>

          <div className="legal-pane" ref={paneRef}>
            <article className="prose">
              <section id="overview" className="anchor">
                <h2>1) Overview</h2>
                <p>
                  Welcome to <strong>Baknay Platform</strong> . This page contains our
                  Terms of Service and Privacy Policy in one place.
                </p>
              </section>

              <section id="terms" className="anchor">
                <h2>2) Terms of Service</h2>

                <h3>2.1 Eligibility</h3>
                <ul>
                  <li>You must be at least 18 years old to register.</li>
                  <li>If you register on behalf of an organization, you confirm you have authority to bind it.</li>
                  <li>You agree to provide accurate information and keep it updated.</li>
                </ul>

                <h3>2.2 Accounts & Access</h3>
                <ul>
                  <li>You are responsible for safeguarding your credentials and access.</li>
                  <li>Do not share accounts or grant unauthorized access.</li>
                  <li>We may suspend or terminate access for policy/security violations.</li>
                </ul>

                <h3>2.3 Business & User Responsibilities</h3>
                <ul>
                  <li>Businesses are solely responsible for the accuracy of data they input or upload.</li>
                  <li>
                    You must comply with applicable laws and regulations in your jurisdictions (e.g., tax, reporting,
                    AML/CFT obligations). Baknay is a dashboard, not a financial institution.
                  </li>
                </ul>

                <h3>2.4 Acceptable Use</h3>
                <ul>
                  <li>No illegal, fraudulent, or harmful activities.</li>
                  <li>No reverse engineering, scraping, or exploiting vulnerabilities.</li>
                  <li>No infringing content or malicious code.</li>
                </ul>

                <h3>2.5 Intellectual Property</h3>
                <p>
                  All materials, designs, and code on Baknay are owned by <strong>Baknay Platform</strong>.
                  You receive a limited, non-exclusive, non-transferable license to use the Platform
                  for business management purposes.
                </p>

                <h3>2.6 Disclaimers & Limitation of Liability</h3>
                <p>
                  Baknay is provided <em>“as-is”</em> and <em>“as-available”</em>. We do not guarantee uninterrupted or
                  error-free operation. To the fullest extent permitted by law, Baknay is not liable for data inaccuracies
                  entered by users, business losses from reliance on displayed information, or indirect, incidental, or
                  consequential damages.
                </p>

                <h3>2.7 Changes to Terms</h3>
                <p>
                  We may update these Terms from time to time. Material changes will be communicated via in-product notice
                  or email. Continued use after changes constitutes acceptance.
                </p>
              </section>

              {/* Privacy */}
              <section id="privacy" className="anchor">
                <h2>3) Privacy Policy (GDPR)</h2>

                <h3>3.1 Data We Collect</h3>
                <ul>
                  <li>Registration data (e.g., name, email, business info, contact details).</li>
                  <li>Usage data (e.g., logins, timestamps, actions for security/audit).</li>
                  <li>Technical data (e.g., IP address, device/browser, country).</li>
                </ul>
                <p>
                  We do <strong>not</strong> collect end-customer banking credentials. Operational data entered by
                  businesses remains under the business’s control.
                </p>

                <h3>3.2 Purposes of Processing</h3>
                <ul>
                  <li>Operate, secure, and improve the Platform.</li>
                  <li>Authenticate users and manage sessions.</li>
                  <li>Provide support and service communications.</li>
                  <li>Analyze usage for performance and reliability.</li>
                </ul>

                <h3>3.3 Legal Bases</h3>
                <ul>
                  <li><strong>Contract</strong> (to provide the service you requested).</li>
                  <li><strong>Legitimate interests</strong> (security, quality, improvement).</li>
                  <li><strong>Consent</strong> where required (e.g., certain cookies/communications).</li>
                </ul>

                <h3>3.4 Storage, Security & International Transfers</h3>
                <p>
                  Data is stored in the EU or in jurisdictions with adequate protection. We apply technical and
                  organizational measures to protect against unauthorized access, loss, or misuse. Where data is transferred
                  outside the EU/EEA, we use appropriate safeguards (e.g., Standard Contractual Clauses).
                </p>

                <h3>3.5 Retention</h3>
                <p>
                  We retain personal data only as long as necessary to provide the service or as required by law. You can
                  request deletion—subject to legal/operational constraints—by contacting us (see Contact).
                </p>

                <h3>3.6 Your Rights</h3>
                <ul>
                  <li>Access, rectify, or erase your personal data.</li>
                  <li>Restrict or object to processing; request data portability.</li>
                  <li>Withdraw consent at any time (where processing relies on consent).</li>
                  <li>Lodge a complaint with your local data protection authority.</li>
                </ul>

                <h3 id="cookies">3.7 Cookies</h3>
                <p>
                  We may use essential and analytics cookies. You can control non-essential cookies via browser settings or
                  a cookie banner (if available).
                </p>

                <h3>3.8 Changes to this Policy</h3>
                <p>
                  We may update this Privacy Policy. Material changes will be communicated via in-product notice or email.
                </p>
              </section>

              {/* Contact & Law */}
              <section id="contact" className="anchor">
                <h2>4) Contact & Governing Law</h2>
                <p>
                  Questions? <strong>contact@baknay.com</strong>
                </p>
                <p>
                  These Terms are governed by the laws of <strong>Sweden</strong>. Disputes are subject to the exclusive
                  jurisdiction of Swedish courts.
                </p>
              </section>

              {/* Back to top */}
              <div className="to-top-wrap">
                <a href="#overview" onClick={(e)=>onTocClick(e,"overview")} className="to-top">Back to top ↑</a>
              </div>
            </article>
          </div>
        </div>
      </main>

      <AppFooter className="legal-footer" />
    </>
  );
}

const STYLES = `
/* Container & typography */
.legal-shell { max-width: 1120px; margin: 0 auto; padding: 24px 16px; }
.legal-header h1 { font-size: clamp(1.6rem, 2.5vw, 2.25rem); margin: 0 0 6px; }
.muted { opacity: .75; font-size: .9rem; }

/* Grid layout */
.legal-grid {
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 24px;
}
@media (max-width: 920px) {
  .legal-grid { grid-template-columns: 1fr; }
}

/* Sticky TOC */
.legal-toc { position: sticky; top: 16px; height: fit-content; }
.card { background: var(--ui-panel-bg, rgba(255,255,255,.06)); border: 1px solid var(--ui-ring, rgba(255,255,255,.15)); border-radius: 14px; overflow: hidden; }
.card-header { padding: 12px 14px; font-weight: 600; background: var(--ui-panel-head, rgba(255,255,255,.04)); border-bottom: 1px solid var(--ui-ring, rgba(255,255,255,.12)); }
.toc-list { margin: 10px 0 12px; padding: 0 14px 14px 28px; }
.toc-list li { margin: 8px 0; }
.toc-list a { color: var(--ui-fg, inherit); text-decoration: none; }
.toc-list a:hover { text-decoration: underline; }
.toc-list a.active { font-weight: 600; text-decoration: underline; }

/* Scrollable pane */
.legal-pane {
  height: calc(var(--app-viewport-height, 100%) - 8rem);        /* forces its own scrollbar */
  overflow-y: auto;
  scroll-behavior: smooth;
  padding-right: 8px;
  padding-bottom: calc(18px + var(--app-safe-area-bottom));
  border: 1px solid var(--ui-ring, rgba(255,255,255,.12));
  border-radius: 14px;
  background: var(--ui-surface, rgba(255,255,255,.02));
}

/* Article styling */
.prose { padding: 20px 18px; line-height: 1.65; }
.prose h2 { font-size: clamp(1.25rem, 2vw, 1.6rem); margin: 20px 0 8px; }
.prose h3 { font-size: clamp(1.05rem, 1.6vw, 1.2rem); margin: 16px 0 6px; }
.prose p { margin: 10px 0; }
.prose ul { padding-left: 22px; margin: 8px 0 12px; }
.prose li { margin: 6px 0; }

/* Anchor offset (if you have a sticky site header) */
.anchor { scroll-margin-top: 80px; }

/* Snippet & back-to-top */
.snippet { background: rgba(127,127,127,.08); border: 1px solid rgba(127,127,127,.25); border-radius: 10px; padding: 12px; overflow-x: auto; }
.to-top-wrap { display: flex; justify-content: flex-end; margin-top: 24px; }
.to-top { display: inline-block; padding: 10px 12px; border-radius: 10px; border: 1px solid var(--ui-ring, rgba(255,255,255,.15)); text-decoration: none; }
.to-top:hover { background: rgba(127,127,127,.08); }

.legal-footer { --app-footer-max-width: 1120px; }
`;
